<template>

    <div class="p-3 rounded-lg shadow-sm bg-white mb-2">
        <Alerts :_success="success" :_error="error" :_loading="wait"></Alerts>

        <div v-if="isReview()" class="alert alert-danger">
            <h6 class="font-weight-bold">Your request was declined</h6>
            {{ request.approval.decline_remarks }}
        </div>

        <div v-if="parseInt(unclosed) >= 3" class="alert alert-danger">
            You have {{ unclosed }} disbursed ERPs.
            Please close them before creating a new one
        </div>

        <form @submit.prevent="saveRequest()">

            <div class="form-group row">
                <label class="col-md-3"></label>
                <div class="col-md-7">
                    <div v-if="wait && progress > 0" class="progress">
                        <div class="progress-bar bg-success progress-bar-striped progress-bar-animated"
                             :style="'width:'+progress+'%'">{{ progress }}%
                        </div>
                    </div>
                </div>
            </div>

            <div class="form-group row">
                <label class="col-md-3"></label>
                <div class="col-md-7">
                    <h5 class="font-weight-bold">
                        <span class="text-success font-weight-bold">Create Request</span><br>
                        <span v-if="isReview()" class="text-danger">(A Review)</span>
                    </h5>
                </div>
            </div>


            <div class="form-group row">
                <label for="title" class="col-md-3 col-form-label text-md-right">I Need</label>
                <div class="col-md-7">
                <textarea class="form-control mb-2" id="title" ref="title" v-model="request.title"
                          placeholder="Describe what you need"
                          :disabled="wait" rows="4" required :maxlength="words"></textarea>

                    <div class="text-muted mb-3">
                        Describe what you need in max of {{ words }} words.
                        Remaining <b>{{ wordCount }} words</b>
                    </div>
                </div>
            </div>

            <div class="form-group row">
                <label for="amount" class="col-md-3 col-form-label text-md-right">Estimated Cost
                    ({{ request.currency }})</label>
                <div class="col-md-7">
                    <input type="number" id="amount" class="form-control mb-2" v-model="request.amount"
                           min=".50"
                           max="100000000" step=".01"
                           placeholder="Estimated cost" :disabled="wait" required>
                    <span class="text-muted">Request Estimated Amount, do not use commas or spaces</span>
                </div>
            </div>

            <div class="form-group row">
                <label for="currency" class="col-md-3 col-form-label text-md-right">Currency</label>
                <div class="col-md-7">
                    <select id="currency" class="form-control mb-2" v-model="request.currency"
                            :disabled="wait" required>
                        <option>USD</option>
                        <option>KES</option>
                        <option>EUR</option>
                        <option>CAD</option>
                    </select>
                    <span class="text-muted">Amount will be converted to USD (rate 100)</span>
                </div>
            </div>

            <div class="form-group row">
                <label for="expected_date" class="col-md-3 col-form-label text-md-right">Expected Date</label>
                <div class="col-md-7">
                    <Datepicker id="expected_date" :bootstrap-styling="true" :disabledDates="disabledDates"
                                v-model="request.date" maximum-view="day" :disabled="wait"
                                placeholder="Select date" :required="true"></Datepicker>
                </div>
            </div>

            <div class="form-group row">
                <label class="typo__label col-md-3 col-form-label text-md-right">Tag Users</label>
                <div class="col-md-7">
                    <div class="custom-control custom-switch custom-control-inline mb-1">
                        <input type="checkbox" class="custom-control-input" id="tag_all_users" v-model="tag_all_users">
                        <label class="custom-control-label" for="tag_all_users">Tag all users</label>
                    </div>
                    <multiselect
                        class="mb-2"
                        v-model="tagged_users"
                        :options="users"
                        :disabled="wait"
                        :multiple="true"
                        track-by="id"
                        :preserve-search="true"
                        :hide-selected="true"
                        placeholder="Select user to tag" label="name" @input="setTaggedUsersIds">
                    </multiselect>
                    <span class="text-muted">Tagged users can view your ERP but cannot edit</span>
                </div>
            </div>

            <div class="form-group row">
                <label class="col-md-3 col-form-label text-md-right">Supporting Document</label>
                <div class="col-md-7">
                    <div class="custom-control custom-radio custom-control-inline">
                        <input type="radio" class="custom-control-input" id="none" name="doc" value="none"
                               v-model="request.document">
                        <label class="custom-control-label" for="none">None</label>
                    </div>
                    <div class="custom-control custom-radio custom-control-inline">
                        <input type="radio" class="custom-control-input" id="doc" name="doc" value="doc"
                               v-model="request.document">
                        <label class="custom-control-label" for="doc">Attach</label>
                    </div>
                    <div class="custom-control custom-radio custom-control-inline">
                        <input type="radio" class="custom-control-input" id="link" name="doc" value="link"
                               v-model="request.document">
                        <label class="custom-control-label" for="link">Add Link</label>
                    </div>

                    <div class="mt-3">
                        <div v-if="request.document==='none'" class="text-muted p-2 border rounded-lg">
                            Please note that you must attach at least one document for your request to be approved. <br>
                            You can attach now or attach later
                        </div>

                        <div v-if="request.document=='doc'">

                            <!--  File upload -->
                            <div class="mb-2">
                                <div class="custom-file mb-2">
                                    <input type="file" class="custom-file-input" ref="fileInput"
                                           @change="handleFileSelection"
                                           multiple id="customFile"
                                           size="10485760"
                                           :accept="allowed_files">
                                    <!--   Max file input 10MB-->

                                    <label class="custom-file-label" for="customFile">Select files to upload</label>
                                </div>
                                <span class="text-muted">Allowed files {{ allowed_files }} |  Max size 10MB</span>

                                <table class="table table-bordered table-sm">
                                    <tr v-for="(file, index) in selectedFilesList" :key="index">
                                        <td>{{ file.name }}</td>
                                        <td>
                                            <a href="#" class="btn btn-danger btn-sm"
                                               @click="removeFileFromList(index)">
                                                Remove
                                            </a>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                            <!--  End of file upload -->


                            <div v-if="docs.length" class="p-4 border">
                                <h5 class="font-weight-bold">
                                    Documents <span class="text-muted">({{ docs.length }})</span>
                                </h5>
                                <table class="table table-sm table-borderless">
                                    <tr v-for="doc in docs" :key="doc.id">
                                        <td class="text-right w-50"><a :href="doc.url" target="_blank">{{
                                                doc.title
                                            }}</a> :
                                        </td>
                                        <td>
                                            <img v-if="doc.type=='img'" :src="doc.url" style="height: 50px"
                                                 class="rounded-lg">
                                            <b v-if="doc.type=='pdf'">PDF Document</b>
                                        </td>
                                    </tr>
                                </table>
                            </div>
                        </div>

                        <input v-if="request.document=='link'" type="url" v-model="request.memo"
                               class="form-control" placeholder="Link to supporting document" required>
                    </div>
                </div>
            </div>

            <div class="form-group row">
                <label class="col-md-3"></label>
                <div class="col-md-7">
                    <button class="btn btn-success mr-2" :disabled="wait">
                        <template v-if="wait">Please wait...</template>
                        <template v-else>Submit Request</template>
                    </button>
                    <router-link to="/requests" class="btn btn-outline-secondary">Go Back</router-link>
                </div>
            </div>

        </form>
    </div>
</template>

<script>
import Alerts from "@/components/Alerts";
import Datepicker from 'vuejs-datepicker'
import Multiselect from 'vue-multiselect'

export default {
    name: 'Request',
    props: ['code', 'auth'],
    components: {Alerts, Datepicker, Multiselect},
    data() {
        return {
            axios: this.$http,
            base_url: "",

            formData: new FormData(),

            selectedFiles: [],
            selectedFilesList: [],
            allowed_files: ".xls, .xlsx, .csv, .jpg, .jpeg, .png, .gif, .pdf",

            words: 460,
            files: '',
            upload_files: [],
            docs: [],
            users: [],
            progress: 0,
            unclosed: 0,

            tagged_users: [],
            tag_all_users: false,

            disabledDates: {
                to: new Date(new Date().setDate(new Date().getDate() - 1)),
                from: new Date(new Date().setDate(new Date().getDate() + 4))
            },

            request: {
                id: '',
                title: '',
                amount: '',
                currency: 'KES',
                budget_id: '',
                date: new Date(),
                tagged: '',
                document: '',
                memo: '',
            }
        }
    },
    watch: {
        tag_all_users() {
            (this.tag_all_users == true) ? this.tagged_users = this.users : this.tagged_users = []
            this.setTaggedUsersIds(this.tagged_users)
        }
    },
    mounted() {
        this.$refs.title.focus();

        this.axios.get(this.base_url + `/requests/unclosed?check=1`).then(res => {
            this.unclosed = res.data.data
        })

        this.axios.get(this.base_url + `/users`).then(res => {
            this.users = res.data.data
        })

        if (this.code) {
            this.axios.get(this.base_url + `/requests/${this.code}`).then(res => {
                this.request = res.data.data
                this.docs = res.data.data.documents
                this.tagged_users = this.request.tagged
                this.setTaggedUsersIds(this.tagged_users)
            })
        }
    },
    computed: {
        wordCount() {
            return (this.words - parseInt(this.request.title.length))
        }
    },
    methods: {
        isReview() {
            return (this.request.id && this.request.status == 'declined')
        },
        setTaggedUsersIds(users) {
            let ids = [];
            users.forEach((i) => {
                ids.push(i.id);
            });
            this.request.tagged = ids;
        },
        saveRequest() {
            window.scroll(0, 0)
            this.wait = true
            this.clear()
            this.progress = 0

            let formData = this.formData

            for (let i = 0; i < this.selectedFilesList.length; i++) {
                formData.append('files[' + i + ']', this.selectedFilesList[i])
            }

            this.request.date = this.formatDate(this.request.date)
            Object.entries(this.request).forEach(([key, value]) => formData.append(key, value))

            this.axios.post(this.base_url + '/request/create', formData, {
                headers: {'Content-Type': 'multipart/form-data'},
                onUploadProgress: function (e) {
                    this.progress = parseInt(Math.round((e.loaded * 100) / e.total))
                }.bind(this)
            }).then(response => {
                this.$toast.success('Request submitted successfully')
                this.progress = 0
                this.success = "Request submitted successfully, redirecting, wait a sec..."
                setTimeout(() => (window.location.href = (`/requests/${response.data.data.code}`)), 500)

            }).catch(error => {
                this.showError(error)
            }).then(() => {
                this.wait = false
            })
        },
        handleFileSelection(event) {
            this.selectedFiles = event.target.files;
            if (this.selectedFiles) {
                for (let i = 0; i < this.selectedFiles.length; i++) {
                    const file = this.selectedFiles[i];
                    if (!this.selectedFilesList.some(f => f.name === file.name)) {
                        this.selectedFilesList.push(file);
                    }
                }
                this.selectedFiles = null;
                this.$refs.fileInput.value = null;
            }
        },
        removeFileFromList(index) {
            this.selectedFilesList.splice(index, 1);
        }
    }
}
</script>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

